import { useEffect, useMemo, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import {
  buildAnnouncementDocument,
  parseAnnouncementMessage,
  type AnnouncementAction,
  type AnnouncementContent,
} from "@/lib/announcements";

interface AnnouncementModalProps {
  announcement: AnnouncementContent | null;
  onAction: (action: AnnouncementAction) => void;
}

export function AnnouncementModal({ announcement, onAction }: AnnouncementModalProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;

  const srcDoc = useMemo(
    () => (announcement ? buildAnnouncementDocument(announcement.html) : ""),
    [announcement],
  );

  useEffect(() => {
    if (!announcement) return;
    const handler = (event: MessageEvent) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      const action = parseAnnouncementMessage(event.data);
      if (action) onActionRef.current(action);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [announcement]);

  const split = announcement?.layout !== "info";

  return (
    <DialogPrimitive.Root
      open={!!announcement}
      onOpenChange={(open) => {
        if (!open) onActionRef.current({ action: "close" });
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-[rgb(17_18_28/0.45)] backdrop-blur-[6px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            frameRef.current?.focus();
          }}
          className={cn(
            "fixed z-[60] overflow-hidden bg-background shadow-[0_24px_64px_rgb(17_18_28/0.28)] outline-none",
            "inset-x-0 bottom-0 h-[min(88dvh,760px)] rounded-t-2xl",
            "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl",
            split
              ? "sm:h-[min(540px,calc(100dvh-48px))] sm:w-[min(880px,calc(100vw-48px))]"
              : "sm:h-[min(480px,calc(100dvh-48px))] sm:w-[min(640px,calc(100vw-48px))]",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          )}
        >
          <DialogPrimitive.Title className="sr-only">{announcement?.title ?? "Anuncio"}</DialogPrimitive.Title>
          {announcement && (
            <iframe
              ref={frameRef}
              key={announcement.id}
              title={announcement.title}
              srcDoc={srcDoc}
              sandbox="allow-scripts"
              referrerPolicy="no-referrer"
              className="h-full w-full border-0 bg-background"
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
